import React, { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext.tsx';

const SEEN_KEY = 'pulse_notification_prompt_seen';

const readSeen = () => {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
};

const writeSeen = () => {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    // private window: the card may come back next visit, which is harmless
  }
};

/**
 * Asks once, the first time someone uses the app, whether to turn on desktop notifications.
 * Browsers only show their own permission dialog from a click, so this card carries the click.
 * Afterwards it can always be changed from the account menu (Desktop notifications).
 */
export const NotificationPermissionPrompt: React.FC = () => {
  const { browserPermission, requestNotificationPermission } = useNotifications();
  const [visible, setVisible] = useState(false);
  const supported = typeof window !== 'undefined' && 'Notification' in window;

  useEffect(() => {
    if (!supported || browserPermission !== 'default' || readSeen()) return;
    // Let the screen settle before asking
    const timer = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(timer);
  }, [supported, browserPermission]);

  useEffect(() => {
    if (browserPermission !== 'default') setVisible(false);
  }, [browserPermission]);

  if (!visible) return null;

  const dismiss = () => {
    writeSeen();
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Turn on desktop notifications"
      className="fixed bottom-24 right-5 z-[60] w-[320px] max-w-[calc(100vw-2.5rem)] bg-white dark:bg-[#181b20] border border-[#d5d8de] dark:border-surface-700 rounded-2xl shadow-2xl p-4 animate-in fade-in slide-in-from-bottom-3 duration-200"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Not now"
        className="absolute top-2.5 right-2.5 p-1 rounded-lg text-surface-400 hover:text-surface-700 dark:hover:text-white hover:bg-surface-100 dark:hover:bg-surface-800"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-orange-500/15 text-orange-600 flex items-center justify-center shrink-0">
          <Bell className="w-4.5 h-4.5" />
        </div>
        <div className="min-w-0 pr-4">
          <h4 className="text-sm font-extrabold text-[#121417] dark:text-white">Turn on notifications?</h4>
          <p className="text-xs text-[#667085] dark:text-surface-400 mt-1 leading-relaxed">
            Get an alert for new messages, mentions and meetings, even when Pulse is in another tab.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-3.5">
        <button
          type="button"
          onClick={dismiss}
          className="px-3 py-1.5 rounded-xl text-xs font-bold text-[#475467] dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800"
        >
          Not now
        </button>
        <button
          type="button"
          onClick={async () => {
            writeSeen();
            setVisible(false);
            await requestNotificationPermission();
          }}
          className="px-3.5 py-1.5 rounded-xl text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white shadow-sm"
        >
          Allow
        </button>
      </div>
    </div>
  );
};
